/**
 * Billing countries, keyed by ISO-3166-1 alpha-2.
 *
 * The code is what gets stored in `clients.country_code` and forwarded to
 * Stripe (`customer.address.country`); `clients.country` keeps the display name
 * so existing views and the AVV renderer stay unchanged.
 *
 * Scope is EU-27 plus the neighbours we actually invoice into. Extend the list
 * rather than falling back to free text — a country without a code cannot be
 * pushed to Stripe.
 */
export interface CountryOption {
  code: string;
  de: string;
  en: string;
  /** EU VAT territory — gates the reverse-charge toggle. */
  eu: boolean;
}

export const HOME_COUNTRY_CODE = 'DE';

export const COUNTRIES: CountryOption[] = [
  { code: 'AT', de: 'Österreich', en: 'Austria', eu: true },
  { code: 'BE', de: 'Belgien', en: 'Belgium', eu: true },
  { code: 'BG', de: 'Bulgarien', en: 'Bulgaria', eu: true },
  { code: 'CH', de: 'Schweiz', en: 'Switzerland', eu: false },
  { code: 'CY', de: 'Zypern', en: 'Cyprus', eu: true },
  { code: 'CZ', de: 'Tschechien', en: 'Czechia', eu: true },
  { code: 'DE', de: 'Deutschland', en: 'Germany', eu: true },
  { code: 'DK', de: 'Dänemark', en: 'Denmark', eu: true },
  { code: 'EE', de: 'Estland', en: 'Estonia', eu: true },
  { code: 'ES', de: 'Spanien', en: 'Spain', eu: true },
  { code: 'FI', de: 'Finnland', en: 'Finland', eu: true },
  { code: 'FR', de: 'Frankreich', en: 'France', eu: true },
  { code: 'GB', de: 'Vereinigtes Königreich', en: 'United Kingdom', eu: false },
  { code: 'GR', de: 'Griechenland', en: 'Greece', eu: true },
  { code: 'HR', de: 'Kroatien', en: 'Croatia', eu: true },
  { code: 'HU', de: 'Ungarn', en: 'Hungary', eu: true },
  { code: 'IE', de: 'Irland', en: 'Ireland', eu: true },
  { code: 'IT', de: 'Italien', en: 'Italy', eu: true },
  { code: 'LI', de: 'Liechtenstein', en: 'Liechtenstein', eu: false },
  { code: 'LT', de: 'Litauen', en: 'Lithuania', eu: true },
  { code: 'LU', de: 'Luxemburg', en: 'Luxembourg', eu: true },
  { code: 'LV', de: 'Lettland', en: 'Latvia', eu: true },
  { code: 'MT', de: 'Malta', en: 'Malta', eu: true },
  { code: 'NL', de: 'Niederlande', en: 'Netherlands', eu: true },
  { code: 'NO', de: 'Norwegen', en: 'Norway', eu: false },
  { code: 'PL', de: 'Polen', en: 'Poland', eu: true },
  { code: 'PT', de: 'Portugal', en: 'Portugal', eu: true },
  { code: 'RO', de: 'Rumänien', en: 'Romania', eu: true },
  { code: 'SE', de: 'Schweden', en: 'Sweden', eu: true },
  { code: 'SI', de: 'Slowenien', en: 'Slovenia', eu: true },
  { code: 'SK', de: 'Slowakei', en: 'Slovakia', eu: true },
  { code: 'US', de: 'Vereinigte Staaten', en: 'United States', eu: false },
];

const BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]));

/** Display name in the active UI language, falling back to the raw code. */
export function countryName(code: string | null | undefined, lang = 'de'): string {
  const entry = BY_CODE.get((code || '').toUpperCase());
  if (!entry) {
    return code || '';
  }
  return lang.startsWith('de') ? entry.de : entry.en;
}

export function isEuCountry(code: string | null | undefined): boolean {
  return !!BY_CODE.get((code || '').toUpperCase())?.eu;
}

export type TaxTreatment =
  | 'domestic'
  | 'reverse_charge'
  | 'eu_missing_vat_id'
  | 'non_eu';

/**
 * How an organisation is taxed — derived from country + VAT ID, never chosen.
 * Mirrors `tax_treatment()` in al-user-management `billing_identity.py`; the
 * server decides, this only tells the user beforehand. Without a country the
 * German rate applies.
 */
export function taxTreatment(
  countryCode: string | null | undefined,
  vatId: string | null | undefined
): TaxTreatment {
  const code = (countryCode || '').toUpperCase();
  if (!code || code === HOME_COUNTRY_CODE) {
    return 'domestic';
  }
  if (isEuCountry(code)) {
    return normalizeVatId(vatId) ? 'reverse_charge' : 'eu_missing_vat_id';
  }
  return 'non_eu';
}

/** i18n key of the sentence describing a treatment. */
export function taxTreatmentKey(treatment: TaxTreatment): string {
  return `input.taxStatus.${treatment}`;
}

/**
 * Resolve a legacy free-text country ("Deutschland", "Germany") to its code.
 * Mirrors the backfill in al-user-management migration 027 so a row written
 * before the picker existed resolves the same way here and on the server.
 */
export function resolveCountryCode(value: string | null | undefined): string | null {
  const raw = (value || '').trim();
  if (!raw) {
    return null;
  }
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const upper = raw.toUpperCase();
    return BY_CODE.has(upper) ? upper : null;
  }
  const needle = raw.toLowerCase();
  const hit = COUNTRIES.find(
    (c) => c.de.toLowerCase() === needle || c.en.toLowerCase() === needle
  );
  return hit ? hit.code : null;
}

/**
 * VAT ID format per country, mirrored from
 * `al-user-management/app/billing_identity.py::_VAT_PATTERNS` so the portal
 * rejects a typo before the request goes out. The backend re-validates — this
 * is convenience, not the gate.
 */
const VAT_PATTERNS: Record<string, RegExp> = {
  AT: /^ATU\d{8}$/,
  BE: /^BE[01]\d{9}$/,
  BG: /^BG\d{9,10}$/,
  CY: /^CY\d{8}[A-Z]$/,
  CZ: /^CZ\d{8,10}$/,
  DE: /^DE\d{9}$/,
  DK: /^DK\d{8}$/,
  EE: /^EE\d{9}$/,
  ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
  FI: /^FI\d{8}$/,
  FR: /^FR[A-Z0-9]{2}\d{9}$/,
  GR: /^(EL|GR)\d{9}$/,
  HR: /^HR\d{11}$/,
  HU: /^HU\d{8}$/,
  IE: /^IE(\d{7}[A-W]{1,2}|\d[A-Z+*]\d{5}[A-W])$/,
  IT: /^IT\d{11}$/,
  LT: /^LT(\d{9}|\d{12})$/,
  LU: /^LU\d{8}$/,
  LV: /^LV\d{11}$/,
  MT: /^MT\d{8}$/,
  NL: /^NL\d{9}B\d{2}$/,
  PL: /^PL\d{10}$/,
  PT: /^PT\d{9}$/,
  RO: /^RO\d{2,10}$/,
  SE: /^SE\d{12}$/,
  SI: /^SI\d{8}$/,
  SK: /^SK\d{10}$/,
  GB: /^GB(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
  CH: /^CHE\d{9}(MWST|TVA|IVA)?$/,
  NO: /^NO\d{9}(MVA)?$/,
};

/** Uppercase and drop the separators people paste ("DE 123 456 789"). */
export function normalizeVatId(value: string | null | undefined): string {
  return (value || '').replace(/[\s.\-/]/g, '').toUpperCase();
}

/** null = valid (including empty); otherwise an i18n key describing the problem. */
export function vatIdError(
  value: string | null | undefined,
  countryCode?: string | null
): string | null {
  const normalized = normalizeVatId(value);
  if (!normalized) {
    return null;
  }
  if (normalized.length < 4 || !/^[A-Z]{2}/.test(normalized)) {
    return 'errors.vatId.prefix';
  }
  const prefix = normalized.substring(0, 2);
  // Greece files VAT under EL but is ISO 'GR'.
  const lookup = prefix === 'EL' ? 'GR' : prefix;

  const pattern = VAT_PATTERNS[lookup];
  if (pattern && !pattern.test(normalized)) {
    return 'errors.vatId.format';
  }
  if (countryCode && lookup !== countryCode.toUpperCase()) {
    return 'errors.vatId.countryMismatch';
  }
  return null;
}
